import React, { createContext, useContext, useState } from 'react';
import { useAuth } from './AuthContext';
import { useEnvironment } from './EnvironmentContext';
import { flowService } from '../services/flowService';
import { launchEmbeddedSignup, loginWithFacebookForToken } from '../utils/facebookSDK';
import { track } from '../utils/analytics';

// Scopes requested via Embedded Signup. whatsapp_business_messaging is required to send
// flow messages (including on coexistence numbers); whatsapp_business_manage_events lets us
// send CTWA conversion events (Conversions API); the others manage the WABA.
const WABA_SCOPE = 'whatsapp_business_management,whatsapp_business_messaging,business_management,whatsapp_business_manage_events';

const WhatsAppConnectionContext = createContext();

export const useWhatsAppConnection = () => {
  const context = useContext(WhatsAppConnectionContext);
  if (!context) {
    throw new Error('useWhatsAppConnection must be used within a WhatsAppConnectionProvider');
  }
  return context;
};

export const WhatsAppConnectionProvider = ({ children }) => {
  const { authenticatedFetch } = useAuth();
  const { selectedEnvironment, getWabaConnection, connectWabaToEnvironment: connectToEnv } = useEnvironment();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Get the current environment's WABA connection
   */
  const getCurrentConnection = () => {
    if (!selectedEnvironment) {
      return null;
    }
    return getWabaConnection(selectedEnvironment.id);
  };

  /**
   * Launch WhatsApp Embedded Signup.
   *
   * Meta hosts the account-selection / coexistence QR-scan UI. Returns the selected
   * WABA along with an authorization code that the backend exchanges for a token.
   *
   * @returns {Promise<{success: boolean, code: string, wabaId: string, phoneNumberId: string|null, accountType: string}>}
   */
  const initiateFacebookLogin = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await launchEmbeddedSignup();
      console.log(`Embedded Signup completed for WABA ${result.wabaId} (${result.accountType})`);
      setLoading(false);
      return { success: true, ...result };
    } catch (error) {
      console.error('Embedded Signup failed:', error);
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  /**
   * Classic Facebook login + server-side WABA discovery, the pre-Embedded-Signup
   * connect path, kept as a second option because ES onboarding requires Meta
   * Advanced Access while classic OAuth works with standard access for app-role
   * users. Returns the discovered WABAs (each carrying the access token) for the
   * user to pick from.
   *
   * @returns {Promise<{success: boolean, wabas: Array<{wabaId: string, wabaName: string, businessId: string, isValid: boolean, accessToken: string}>}>}
   */
  const initiateClassicFacebookLogin = async () => {
    setLoading(true);
    setError(null);

    try {
      const { accessToken } = await loginWithFacebookForToken();

      const response = await authenticatedFetch('/api/whatsapp/discover', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessToken })
      });
      if (!response.ok) {
        throw new Error('Could not discover WhatsApp Business Accounts for this Facebook account');
      }
      const discovery = await response.json();
      const wabas = (discovery.wabas || []).map((w) => ({
        wabaId: w.id,
        wabaName: w.name,
        businessId: w.businessId,
        isValid: w.isValid,
        errorMessage: w.errorMessage,
        accessToken
      }));

      if (wabas.length === 0) {
        throw new Error('No WhatsApp Business Accounts found. Ensure your Facebook account has access to at least one WABA.');
      }

      setLoading(false);
      return { success: true, wabas };
    } catch (error) {
      console.error('Classic Facebook login failed:', error);
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  /**
   * Connect a WABA to the current environment.
   *
   * Two credential shapes (exactly one): an Embedded Signup result carrying `code`
   * (the backend exchanges it for a token), or a classic-login/BYO selection
   * carrying `accessToken` directly. The backend resolves the WABA name / phone
   * numbers from the Graph API either way.
   *
   * @param {{code?: string, accessToken?: string, wabaId: string, wabaName?: string, businessId?: string, phoneNumberId?: string|null, accountType?: string}} onboarding
   * @param {{environmentId?: string, replace?: boolean}} [options] - environmentId: the
   *   environment to connect, when it is not the selected one (Settings lists every environment,
   *   and connecting from a card used to land on whichever was selected); replace: swap the
   *   environment's current number for this one
   */
  const connectWabaToEnvironment = async (onboarding, { environmentId, replace = false } = {}) => {
    const targetId = environmentId || selectedEnvironment?.id;
    if (!targetId) {
      throw new Error('No environment selected. Please select an environment first.');
    }

    setLoading(true);
    setError(null);

    try {
      console.log('Saving WABA connection to environment...');

      const connection = await connectToEnv(targetId, {
        wabaId: onboarding.wabaId,
        wabaName: onboarding.wabaName || null,
        businessId: onboarding.businessId || null,
        phoneNumberId: onboarding.phoneNumberId || null,
        accountType: onboarding.accountType || 'production',
        code: onboarding.code || null,
        accessToken: onboarding.accessToken || null,
        scope: WABA_SCOPE
      }, { replace });

      // Activation funnel: a WABA is now connected to the environment. Fired only on the explicit
      // user connect action (not on render), so no de-dup guard is needed. No raw wabaId/token,
      // just account shape, to keep tenant identifiers off the analytics stream.
      track('waba_connected', { accountType: onboarding.accountType || 'production' });
      // Launch funnel event (docs/launch/ws-I.md), same fact, the funnel's own name.
      track('number_connected', { accountType: onboarding.accountType || 'production', replaced: replace });

      return connection;
    } catch (err) {
      console.error('Error connecting WABA:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // ===================================================================
  // FLOW OPERATIONS - Direct Facebook API
  // These use the environment's WABA connection
  // ===================================================================

  /**
   * Deploy a flow to WhatsApp Business API (via backend)
   */
  const deployFlow = async (flowJson, flowName, existingFlowId = null) => {
    if (!selectedEnvironment) {
      throw new Error('No environment selected');
    }

    try {
      setLoading(true);

      if (existingFlowId) {
        // Update existing flow
        const result = await flowService.updateFlow(
          selectedEnvironment.id,
          existingFlowId,
          { name: flowName, flowJson },
          authenticatedFetch
        );

        // Deploy the updated flow
        const deployed = await flowService.deployFlow(
          selectedEnvironment.id,
          result.id,
          authenticatedFetch
        );

        return deployed;
      } else {
        // Create new flow
        const created = await flowService.createFlow(
          selectedEnvironment.id,
          { name: flowName, flowJson },
          authenticatedFetch
        );

        // Deploy the new flow
        const deployed = await flowService.deployFlow(
          selectedEnvironment.id,
          created.id,
          authenticatedFetch
        );

        return deployed;
      }
    } catch (error) {
      console.error('Error deploying flow:', error);
      throw new Error(`Failed to deploy flow: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Delete a single flow (via backend)
   */
  const deleteFlow = async (flowId) => {
    if (!selectedEnvironment) {
      throw new Error('No environment selected');
    }

    try {
      setLoading(true);
      await flowService.deleteFlow(
        selectedEnvironment.id,
        flowId,
        authenticatedFetch
      );
      console.log(`Successfully deleted flow ${flowId}`);
    } catch (error) {
      console.error(`Error deleting flow ${flowId}:`, error);
      throw new Error(`Failed to delete flow: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Delete all flows (via backend)
   */
  const deleteAllFlows = async () => {
    if (!selectedEnvironment) {
      throw new Error('No environment selected');
    }

    try {
      setLoading(true);

      // Fetch all flows
      const flows = await flowService.listFlows(
        selectedEnvironment.id,
        authenticatedFetch
      );

      console.log(`Found ${flows.length} flows to delete`);

      // Delete each flow
      const results = await Promise.allSettled(
        flows.map(flow =>
          flowService.deleteFlow(
            selectedEnvironment.id,
            flow.id,
            authenticatedFetch
          )
        )
      );

      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      console.log(`Delete summary: ${successful} successful, ${failed} failed`);

      return {
        total: flows.length,
        successful,
        failed,
        flows: flows.map(f => ({ id: f.id, name: f.name }))
      };
    } catch (error) {
      console.error('Error deleting all flows:', error);
      throw new Error(`Failed to delete flows: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch flows from WhatsApp (via backend)
   */
  const fetchFlows = async () => {
    if (!selectedEnvironment) {
      throw new Error('No environment selected');
    }

    try {
      setLoading(true);

      const flows = await flowService.listFlows(
        selectedEnvironment.id,
        authenticatedFetch
      );

      console.log('Flows fetched successfully:', flows.length);
      return flows;
    } catch (error) {
      console.error('Error fetching flows:', error);
      throw new Error(`Failed to fetch flows: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Publish a flow (via backend)
   */
  const publishFlow = async (flowId) => {
    if (!selectedEnvironment) {
      throw new Error('No environment selected');
    }

    try {
      setLoading(true);

      const result = await flowService.publishFlow(
        selectedEnvironment.id,
        flowId,
        authenticatedFetch
      );

      console.log(`Successfully published flow ${flowId}`);
      return result;
    } catch (error) {
      console.error(`Error publishing flow ${flowId}:`, error);
      throw new Error(`Failed to publish flow: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Context value
  const value = {
    // Connection state (environment-based)
    selectedConnection: getCurrentConnection(),
    loading,
    error,
    isConnected: !!getCurrentConnection(),

    // Actions
    initiateFacebookLogin,      // Launch Embedded Signup (returns the onboarded WABA + auth code)
    initiateClassicFacebookLogin, // Classic OAuth + server-side discovery (returns WABAs to pick from)
    connectWabaToEnvironment,   // Connect the onboarded WABA to the environment

    // Flow operations (via backend)
    deployFlow,
    deleteFlow,
    deleteAllFlows,
    fetchFlows,
    publishFlow,

    // Utilities
    clearError: () => setError(null),

    // Backward compatibility helpers
    selectedWABA: getCurrentConnection(),
    isLoading: loading,
    connectionStatus: getCurrentConnection() ? 'connected' : 'disconnected'
  };

  return (
    <WhatsAppConnectionContext.Provider value={value}>
      {children}
    </WhatsAppConnectionContext.Provider>
  );
};

export default WhatsAppConnectionContext;
